/**
 * The full correction of a finished challenge (D-106), shared by the web and the phone: for
 * each question, the answer given, the right answer and the explanation (empty: « to come »).
 * TCF rows carry `choice` and `answer` (indexes of `options`); IELTS rows are course exercises
 * with `response` and `answer` in the form of their type.
 */

import { type ExamPicture, parsePicture, parsePictures } from "./pictures";

export interface ChallengeReviewRow {
  id: string;
  level: string;
  stem: string;
  options: string[];
  given: string;
  right: string;
  answered: boolean;
  correct: boolean;
  explanation: string;
  image: ExamPicture | null;
  optionImages: (ExamPicture | null)[];
  /** TCF rows: the indexes of the option given and of the right one (null otherwise). */
  givenIndex: number | null;
  rightIndex: number | null;
}

function index(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

/** One answer (an index, a list, words, TRUE / FALSE…) as text, with the options it points to. */
export function reviewAnswerText(value: unknown, options: readonly string[]): string {
  if (value === null || value === undefined || value === -1 || value === "") return "";
  if (typeof value === "number") return options[value] ?? String(value);
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  if (Array.isArray(value)) return value.map((v) => reviewAnswerText(v, options)).filter(Boolean).join(" · ");
  if (isObject(value)) return Object.values(value).map((v) => reviewAnswerText(v, options)).filter(Boolean).join(" · ");
  return "";
}

export function parseChallengeReview(value: unknown): ChallengeReviewRow[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).map((row) => {
    const data = isObject(row.data) ? row.data : {};
    const raw = Array.isArray(row.options) ? row.options : Array.isArray(data.options) ? data.options : [];
    const options = raw.map((o) => (isObject(o) ? text(o.text) || text(o.label) : text(o)));
    const given = "choice" in row ? row.choice : row.response;
    return {
      id: text(row.id),
      level: text(row.level),
      stem: text(row.stem) || text(row.prompt),
      options,
      given: reviewAnswerText(given, options),
      right: reviewAnswerText(row.answer, options),
      answered: given !== null && given !== undefined && given !== -1,
      correct: row.correct === true,
      explanation: text(row.explanation),
      image: parsePicture(row.image),
      optionImages: parsePictures(row.option_images),
      givenIndex: index(given),
      rightIndex: index(row.answer),
    };
  });
}
